'use client';

import { ChevronLeft, X } from 'lucide-react';

import { Dialog, DialogContent } from '@kit/ui/dialog';

import { BillingCheckout } from './billing-checkout';

/**
 * Shared post-Buy checkout overlay.
 *
 * Both the navbar BuyCoinsModal and the /pricing page render the
 * BillingCheckout iframe through THIS component once a checkoutToken is
 * obtained, so the moment-of-payment experience is identical across the app.
 *
 * Layout: a Dialog with a fixed-height frame, a "Back" button (returns to the
 * caller's pack-list state), and a close X. Uses `variant="inline"` so the
 * iframe renders inside the Dialog instead of opening a second overlay.
 */
export interface CheckoutOverlayProps {
  /** Token returned by /api/billing/checkout. Renders nothing when null. */
  checkoutToken: string | null;
  /**
   * Called when the user clicks Back (returns to pack list) or closes the
   * Dialog without finishing. The caller should clear its own checkout token
   * state here. NOT called when the purchase succeeds — use `onComplete`.
   */
  onBack: () => void;
  /**
   * Called when BillingCheckout signals completion. Caller usually closes
   * its parent modal and refreshes the user's coin balance here.
   */
  onComplete?: () => void;
  /**
   * Optional: hide the Back button (e.g. when the caller is itself a full
   * page like /pricing where there's no "back to grid" to return to).
   */
  hideBack?: boolean;
}

export function CheckoutOverlay({
  checkoutToken,
  onBack,
  onComplete,
  hideBack,
}: CheckoutOverlayProps) {
  if (!checkoutToken) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onBack();
      }}
    >
      <DialogContent className="max-w-[95vw] overflow-visible border-0 bg-transparent p-0 shadow-none sm:max-w-2xl lg:max-w-3xl [&>button]:hidden">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-[0_0_80px_rgba(236,72,153,0.15)] sm:rounded-3xl">
          {!hideBack && (
            <button
              type="button"
              onClick={onBack}
              className="absolute top-3 left-3 z-20 flex items-center gap-1.5 rounded-full bg-black/90 px-3 py-1.5 text-xs font-medium text-white shadow-lg ring-1 ring-white/20 transition-colors hover:bg-black"
              aria-label="Back"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="absolute top-3 right-3 z-20 rounded-full bg-black/90 p-1.5 text-white shadow-lg ring-1 ring-white/20 transition-colors hover:bg-black"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="h-[600px] sm:h-[700px]">
            <BillingCheckout
              checkoutToken={checkoutToken}
              variant="inline"
              onClose={() => {
                // Delay invalidation by 1s to allow the IPN webhook to arrive
                // and credit coins before the parent re-fetches balance.
                setTimeout(() => {
                  onComplete?.();
                }, 1000);
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
